import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { FormProvider, useForm } from "react-hook-form";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";
import RHFInput from "@/components/form/RHFInput";
import { Card } from "@/components/ui/Card";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import AuthService from "@/services/authService";
import { signupSchema } from "./validation";
import { useBranding } from "@/hooks/useBranding";

const Signup = () => {
  const navigate = useNavigate();
  const currentTheme = useSelector((state) => state.theme.theme);
  const isDark = currentTheme === "dark";
  const { branding } = useBranding();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm({
    resolver: zodResolver(signupSchema),
    mode: "onSubmit",
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onHandleSubmit = methods.handleSubmit(async (data) => {
    try {
      setIsSubmitting(true);

      const response = await AuthService.signup({
        name: data.name,
        email: data.email,
        password: data.password,
      });

      if (!response?.success) {
        toast.error(response?.message || "Unable to create account");
        return;
      }

      // Pending, not live: no session was minted, so there is no profile to
      // load and nowhere to land. The request sits with the administrator.
      toast.success("Your request is with the administrator.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Unable to create account");
    } finally {
      setIsSubmitting(false);
    }
  });

  const brandDisplayName = branding?.displayName;

  useEffect(() => {
    const suffix = brandDisplayName || "IMS People Possible AI";
    document.title = `Sign Up | ${suffix}`;
  }, [brandDisplayName]);

  return (
    <div className="fixed inset-0 overflow-hidden">
      <div
        className={cn(
          "h-full w-full flex items-center justify-center bg-cover bg-center bg-no-repeat px-4",
          isDark ? "bg-[url('/images/home_page_bg_dark.jpg')]" : "bg-[url('/images/home_page_bg_light.jpg')]",
        )}
      >
        {/* dark overlay */}
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm pointer-events-none" aria-hidden />

        {/* modal wrapper */}
        <div className="relative z-10 w-full max-w-[420px] sm:max-w-[460px]">
          <Card
            className={cn(
              "mx-auto flex flex-col rounded-2xl border border-white/25 bg-white/10",
              "backdrop-blur-2xl shadow-[0_20px_80px_rgba(0,0,0,0.45)]",
              "px-4 py-4 sm:px-6 sm:py-6",
              "max-h-[92vh] overflow-hidden",
            )}
          >
            <header className="mb-2 text-center">
              {branding?.logoUrl && (
                <div className="flex justify-center mb-3">
                  <img src={branding.logoUrl} alt={brandDisplayName || "Brand logo"} className="h-10 w-auto object-contain" />
                </div>
              )}
              <div className="inline-flex items-center rounded-full border border-white/25 bg-black/10 px-3 py-[2px] text-[10px] sm:text-[11px] font-light tracking-wide text-text-base/80">
                Local account registration
              </div>
              <h1 className="mt-2 text-lg sm:text-xl font-medium text-text-base">
                {brandDisplayName ? `Create your ${brandDisplayName} account` : "Create your account"}
              </h1>
              <p className="mt-1 text-[11px] sm:text-xs text-text-base/70">Sign up with your email and password.</p>
            </header>

            <FormProvider {...methods}>
              <form onSubmit={onHandleSubmit} className="space-y-3 pt-1 text-sm">
                <RHFInput name="name" label="Full name" placeholder="Enter your full name" type="text" />

                <RHFInput name="email" label="Email" placeholder="you@company.com" type="email" />

                <div className="relative">
                  <RHFInput name="password" label="Password" placeholder="Enter password" type={showPassword ? "text" : "password"} />
                  <ButtonShadcn
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-[46px] h-auto w-auto -translate-y-1/2 text-gray-300 hover:text-gray-100 hover:bg-transparent focus:outline-none transition-colors duration-150"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </ButtonShadcn>
                </div>

                <div className="relative">
                  <RHFInput name="confirmPassword" label="Confirm password" placeholder="Confirm password" type={showConfirmPassword ? "text" : "password"} />
                  <ButtonShadcn
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3 top-[46px] h-auto w-auto -translate-y-1/2 text-gray-300 hover:text-gray-100 hover:bg-transparent focus:outline-none transition-colors duration-150"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </ButtonShadcn>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full rounded-lg
                             bg-gradient-to-r from-indigo-500 to-blue-600
                             hover:from-indigo-600 hover:to-blue-700
                             text-text-base flex items-center justify-center gap-2
                             py-2.5 text-sm transition-all duration-200
                             disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Sign up <ArrowRight size={16} />
                    </>
                  )}
                </Button>
              </form>
            </FormProvider>

            <div className="mt-3 text-center text-[11px] sm:text-xs text-text-base/70">
              Already have an account?{" "}
              <Link to="/login" className="text-indigo-200 hover:text-indigo-100 hover:underline">
                Sign in
              </Link>
            </div>

            <div className="mt-2 text-center text-[10px] sm:text-[11px] text-text-base/60">
              <Link to="/" className="inline-flex items-center gap-1.5 text-indigo-200 hover:text-indigo-100 hover:underline group">
                <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
                Back to home
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Signup;
